import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { clubAPI, lineAssignmentsAPI } from '../lib/api';
import { formatDateRange, getStatusColor, getOrgColor } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Users,
  Calendar,
  MapPin,
  Clock,
  User,
  Mail,
  Target,
  Loader2,
  AlertCircle,
  Building,
  ChevronRight,
  Search,
  Filter,
  X,
  Hash,
  Upload,
  Plus,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClubEvents() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [clubData, setClubData] = useState({ members: [], events: [], club_name: null });
  const [clubAssignments, setClubAssignments] = useState({ assignments: [], registrations: [] });
  
  // Filter states
  const [tournamentFilter, setTournamentFilter] = useState('');
  const [archerFilter, setArcherFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('ALL');
  
  // Upload dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingTournament, setUploadingTournament] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user?.role === 'coach') {
      fetchClubData();
      fetchClubAssignments();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchClubData = async () => {
    setLoading(true);
    try {
      const [membersRes, eventsRes] = await Promise.all([
        clubAPI.getMembers(),
        clubAPI.getEvents()
      ]);
      setClubData({
        members: membersRes.data.members || [],
        events: eventsRes.data.events || [],
        club_name: membersRes.data.club_name || eventsRes.data.club_name,
        member_count: membersRes.data.member_count || 0
      });
    } catch (error) {
      console.error('Failed to fetch club data:', error);
      toast.error('Failed to load club data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubAssignments = async () => {
    try {
      const response = await lineAssignmentsAPI.getClubAssignments();
      setClubAssignments(response.data);
    } catch (error) {
      console.error('Failed to fetch club assignments');
    }
  };

  // Get line assignment for a specific event
  const getLineAssignment = (tournamentId, archerName) => {
    return clubAssignments.assignments?.find(a => 
      a.tournament_id === tournamentId && 
      (archerName ? a.archer_name.toLowerCase().includes(archerName.toLowerCase()) : true)
    );
  };

  // Handle PDF upload for coaches
  const handleUploadPDF = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploading(true);
    try {
      const response = await lineAssignmentsAPI.uploadPDF(uploadingTournament.id, file);
      toast.success(`Extracted ${response.data.total_extracted} line assignments`);
      setUploadDialogOpen(false);
      fetchClubAssignments();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to process PDF';
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openUploadDialog = (tournament) => {
    setUploadingTournament(tournament);
    setUploadDialogOpen(true);
  };

  // Extract unique states from events for the state filter dropdown
  const availableStates = useMemo(() => {
    const states = new Set();
    clubData.events.forEach(event => {
      const location = event.tournament?.location || '';
      // Extract state from location (assumes format like "City, ST" or just state abbreviation)
      const parts = location.split(',');
      const state = parts.length > 1 ? parts[parts.length - 1].trim() : location.trim();
      if (state && state.length >= 2) {
        states.add(state);
      }
    });
    return Array.from(states).sort();
  }, [clubData.events]);

  // Clear all filters
  const clearFilters = () => {
    setTournamentFilter('');
    setArcherFilter('');
    setStateFilter('ALL');
  };

  const hasActiveFilters = tournamentFilter || archerFilter || stateFilter !== 'ALL';

  const getRoleBadge = (role) => {
    const colors = {
      archer: 'bg-green-100 text-green-700',
      parent: 'bg-purple-100 text-purple-700',
    };
    return (
      <Badge variant="outline" className={colors[role] || 'bg-slate-100 text-slate-700'}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  // Group events by tournament with filters applied
  const filteredAndGroupedEvents = useMemo(() => {
    // First, filter events based on search criteria
    let filteredEvents = clubData.events;
    
    // Filter by tournament name
    if (tournamentFilter) {
      const searchLower = tournamentFilter.toLowerCase();
      filteredEvents = filteredEvents.filter(event => 
        event.tournament?.name?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by archer name
    if (archerFilter) {
      const searchLower = archerFilter.toLowerCase();
      filteredEvents = filteredEvents.filter(event => 
        event.archer?.full_name?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by state
    if (stateFilter && stateFilter !== 'ALL') {
      filteredEvents = filteredEvents.filter(event => {
        const location = event.tournament?.location || '';
        return location.toLowerCase().includes(stateFilter.toLowerCase());
      });
    }
    
    // Group filtered events by tournament
    const grouped = filteredEvents.reduce((acc, event) => {
      const tournamentId = event.tournament.id;
      if (!acc[tournamentId]) {
        acc[tournamentId] = {
          tournament: event.tournament,
          archers: []
        };
      }
      acc[tournamentId].archers.push({
        ...event.archer,
        status: event.status,
        event_time: event.event_time,
        notes: event.notes
      });
      return acc;
    }, {});
    
    return Object.values(grouped).sort(
      (a, b) => new Date(a.tournament.start_date) - new Date(b.tournament.start_date)
    );
  }, [clubData.events, tournamentFilter, archerFilter, stateFilter]);

  // Check if user is a coach
  if (user?.role !== 'coach') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Coach Access Only</h2>
            <p className="text-slate-600">
              This page is only available to coaches. Club Events allows coaches to view all archers registered under their club.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!clubData.club_name) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Club Associated</h2>
            <p className="text-slate-600 mb-4">
              You don't have a club/organization set in your profile. Please update your profile with your club name to view your club members.
            </p>
            <Button asChild className="btn-primary">
              <a href="/profile">Update Profile</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Club Events
          </h1>
          <p className="text-slate-600 mt-1">
            View your club members and their tournament registrations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 px-3 py-1">
            <Building className="w-4 h-4 mr-1" />
            {clubData.club_name}
          </Badge>
          <Badge variant="outline" className="bg-slate-100 text-slate-700 px-3 py-1">
            {clubData.member_count} member{clubData.member_count !== 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="events" className="flex items-center gap-2" data-testid="tab-events">
            <Calendar className="w-4 h-4" />
            Upcoming Events ({filteredAndGroupedEvents.length})
          </TabsTrigger>
          <TabsTrigger value="members" className="flex items-center gap-2" data-testid="tab-members">
            <Users className="w-4 h-4" />
            Club Members ({clubData.members.length})
          </TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="mt-6 space-y-4">
          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Filters</span>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="ml-auto text-slate-500 hover:text-slate-700 h-7 px-2"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Tournament Name Filter */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Tournament Name</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Search tournaments..."
                      value={tournamentFilter}
                      onChange={(e) => setTournamentFilter(e.target.value)}
                      className="pl-8 h-9 text-sm"
                      data-testid="filter-tournament"
                    />
                  </div>
                </div>
                
                {/* Archer Name Filter */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Archer Name</Label>
                  <div className="relative">
                    <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Search archers..."
                      value={archerFilter}
                      onChange={(e) => setArcherFilter(e.target.value)}
                      className="pl-8 h-9 text-sm"
                      data-testid="filter-archer"
                    />
                  </div>
                </div>
                
                {/* State Filter */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">State / Location</Label>
                  <Select value={stateFilter} onValueChange={setStateFilter}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-state">
                      <MapPin className="w-4 h-4 text-slate-400 mr-1" />
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All States</SelectItem>
                      {availableStates.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {hasActiveFilters && (
                <p className="text-xs text-slate-500 mt-3">
                  Showing {filteredAndGroupedEvents.length} tournament{filteredAndGroupedEvents.length !== 1 ? 's' : ''} 
                  {' '}with {filteredAndGroupedEvents.reduce((sum, g) => sum + g.archers.length, 0)} archer registration{filteredAndGroupedEvents.reduce((sum, g) => sum + g.archers.length, 0) !== 1 ? 's' : ''}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Events List */}
          {filteredAndGroupedEvents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {hasActiveFilters ? 'No Matching Events' : 'No Registered Events'}
                </h3>
                <p className="text-slate-500">
                  {hasActiveFilters 
                    ? 'Try adjusting your filters to see more results.'
                    : 'None of your club members have registered for any tournaments yet.'}
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" className="mt-4" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredAndGroupedEvents.map((group) => (
                <Card key={group.tournament.id} className="border-slate-200" data-testid={`event-${group.tournament.id}`}>
                  <CardContent className="p-6">
                    {/* Tournament Header */}
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-semibold text-slate-900 text-lg">{group.tournament.name}</h3>
                          <Badge variant="outline" className={getOrgColor(group.tournament.org)}>
                            {group.tournament.org}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-sm text-slate-600">
                          <p className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {formatDateRange(group.tournament.start_date, group.tournament.end_date)}
                          </p>
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {group.tournament.location}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {group.archers.length} archer{group.archers.length !== 1 ? 's' : ''} registered
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        onClick={() => openUploadDialog(group.tournament)}
                        data-testid={`upload-schedule-${group.tournament.id}`}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Upload Schedule
                      </Button>
                    </div>

                    {/* Archers List */}
                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">Registered Archers:</p>
                      <div className="grid gap-3">
                        {group.archers.map((archer, idx) => {
                          const assignment = getLineAssignment(group.tournament.id, archer.full_name);
                          return (
                          <div
                            key={`${archer.id}-${idx}`}
                            className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{archer.full_name}</p>
                                <div className="flex items-center gap-2 text-sm flex-wrap">
                                  {getRoleBadge(archer.role)}
                                  <Badge variant="outline" className={getStatusColor(archer.status)}>
                                    {archer.status}
                                  </Badge>
                                  {/* Line Assignment Badge */}
                                  {assignment && (
                                    <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                                      <Hash className="w-3 h-3 mr-1" />
                                      Line {assignment.line_number}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {assignment?.event_time && (
                                <div className="flex items-center gap-1 text-sm text-indigo-600 font-medium">
                                  <Clock className="w-4 h-4" />
                                  {assignment.event_time}
                                </div>
                              )}
                              {archer.event_time && !assignment?.event_time && (
                                <div className="flex items-center gap-1 text-sm text-primary font-medium">
                                  <Clock className="w-4 h-4" />
                                  {archer.event_time}
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="mt-6">
          {clubData.members.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Club Members Yet</h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  No archers have registered with your club name "{clubData.club_name}". 
                  Make sure archers enter the exact club name when they register.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {clubData.members.map((member) => (
                <Card key={member.id} className="border-slate-200" data-testid={`member-${member.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{member.full_name}</h3>
                          {getRoleBadge(member.role)}
                          <Badge 
                            variant="outline" 
                            className={member.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}
                          >
                            {member.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 text-sm">
                          <Mail className="w-4 h-4" />
                          <span>{member.email}</span>
                        </div>
                        {member.created_at && (
                          <p className="text-xs text-slate-500 mt-1">
                            Joined: {new Date(member.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Schedule PDF Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Tournament Schedule</DialogTitle>
            <DialogDescription>
              Upload a PDF containing line/lane assignments to extract archer schedules for your club.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {uploadingTournament && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{uploadingTournament.name}</p>
                <p className="text-sm text-slate-500">
                  {formatDateRange(uploadingTournament.start_date, uploadingTournament.end_date)}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Schedule PDF</Label>
              <Input
                type="file"
                accept=".pdf"
                ref={fileInputRef}
                onChange={handleUploadPDF}
                disabled={uploading}
                data-testid="club-schedule-pdf-input"
              />
              <p className="text-xs text-slate-500">
                We'll extract archer names, line/lane numbers, and times from the PDF.
                Line assignments will be matched to your club members.
              </p>
            </div>
            
            {uploading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
                <span className="text-slate-600">Processing PDF...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
